import { Button } from '@monorepo/shared/componentsV2/Button';
import {
  IconSlotCell,
  IconSlotColumnHeader,
} from '@monorepo/shared/componentsV2/Table/IconSlotCell';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import ClockCounterClockwiseIcon from '@svg/clock-counter-clockwise.svg';
import { FieldValueVersion } from 'mapistry-shared';
import React from 'react';
import styled from 'styled-components';

export const valueHistoryIconSlotWidth = '1.5rem';

const ValueButton = styled(Button)`
  justify-content: flex-start;
  padding: 0 0.25rem 0 0;
  color: ${({ theme }) => theme.colors.gray333};
  text-align: left;
  text-transform: none;
`;

const UnderlinedSpan = styled.span`
  text-decoration: underline;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;

  & svg {
    width: 1rem;
    height: 1rem;
    margin-right: 0.25rem;
    margin-left: 0.25rem;
  }
`;

type ValueWithHistoryProps = {
  displayValue: React.ReactElement | string;
  fieldName: string;
  onShowHistory: () => void;
  versions: FieldValueVersion[];
};

export function ValueWithHistory({
  displayValue,
  fieldName,
  onShowHistory,
  versions,
}: ValueWithHistoryProps) {
  const icon = (
    <IconContainer>
      <ClockCounterClockwiseIcon aria-hidden />
      <VisuallyHidden>All historical values</VisuallyHidden>
    </IconContainer>
  );

  if (versions.length <= 1) {
    return (
      <IconSlotCell
        icon={icon}
        iconSlotWidth={valueHistoryIconSlotWidth}
        isIconVisible={false}
      >
        {displayValue}
      </IconSlotCell>
    );
  }

  return (
    <ValueButton onClick={onShowHistory} title={`View ${fieldName} history`}>
      <IconSlotCell
        icon={icon}
        iconSlotWidth={valueHistoryIconSlotWidth}
        isIconVisible
      >
        <UnderlinedSpan>{displayValue}</UnderlinedSpan>
      </IconSlotCell>
    </ValueButton>
  );
}

/**
 * Column header for a column whose cells render <ValueWithHistory />. It reserves
 * the same left space as the icon slot so the header aligns with the values.
 */
export function ValueWithHistoryColumnHeader({ label }: { label: string }) {
  return (
    <IconSlotColumnHeader iconSlotWidth={valueHistoryIconSlotWidth}>
      {label}
    </IconSlotColumnHeader>
  );
}
